import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Camera, Upload, ScanFace, RotateCcw, Download, MessageCircle, ShoppingBag, Check } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const waTryon = (nome) =>
  `https://wa.me/393925218114?text=${encodeURIComponent(
    `Ciao Stylottica Ceglie! Ho provato virtualmente "${nome}" sul vostro sito e mi piace: è disponibile in negozio?`
  )}`;

export default function TryOnModal({ product, onClose, experimental = false, products = [], onAdd = null }) {
  const { t } = useLang();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [selfie, setSelfie] = useState(null); // dataURL anteprima
  const [selfieFile, setSelfieFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraErr, setCameraErr] = useState(false);
  // modalità confronto (area test)
  const [mode, setMode] = useState("single");
  const [picked, setPicked] = useState([product.id]);
  const [compareResults, setCompareResults] = useState(null);

  const tryonProducts = products.filter((p) => p.tryon);
  const isCompare = experimental && mode === "compare";

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      stopCamera();
    };
  }, []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  };

  const startCamera = async () => {
    setCameraErr(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOn(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 50);
    } catch {
      setCameraErr(true);
      toast.error(t("tryon_camera_err"));
    }
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      setSelfieFile(new File([blob], "selfie.jpg", { type: "image/jpeg" }));
      setSelfie(canvas.toDataURL("image/jpeg", 0.9));
      stopCamera();
    }, "image/jpeg", 0.9);
  };

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSelfieFile(f);
    setSelfie(URL.createObjectURL(f));
  };

  const togglePick = (id) => {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) return prev;
      return [...prev, id];
    });
  };

  const runTryOn = async () => {
    if (!selfieFile) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("product_id", product.id);
      fd.append("file", selfieFile);
      const r = await axios.post(`${API}/tryon`, fd, { timeout: 150000 });
      setResult(r.data.image);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("tryon_error"));
    } finally {
      setLoading(false);
    }
  };

  const runCompare = async () => {
    if (!selfieFile) return;
    if (picked.length < 2) {
      toast.error(t("tryon_pick_min"));
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("product_ids", picked.join(","));
      fd.append("file", selfieFile);
      const r = await axios.post(`${API}/tryon/compare`, fd, { timeout: 300000 });
      setCompareResults(r.data.results);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("tryon_error"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setCompareResults(null);
    setSelfie(null);
    setSelfieFile(null);
    setPicked([product.id]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const addToCart = (p) => {
    if (!onAdd) return;
    onAdd(p);
    toast.success(t("tryon_added"));
  };

  const ActionRow = ({ p, img, prefix }) => (
    <div className="flex flex-wrap gap-2">
      <a
        href={waTryon(p.nome)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${prefix}-wa`}
        className="flex items-center gap-1.5 rounded-full bg-[#25D366] text-white text-[10px] uppercase tracking-[0.12em] px-4 py-2.5 hover:opacity-85 transition-opacity"
      >
        <MessageCircle size={13} /> WhatsApp
      </a>
      <a
        href={img}
        download={`prova-${p.nome.replace(/\s+/g, "-").toLowerCase()}.png`}
        data-testid={`${prefix}-dl`}
        className="flex items-center gap-1.5 rounded-full border border-[#1A1B18]/30 text-[10px] uppercase tracking-[0.12em] px-4 py-2.5 hover:bg-[#1A1B18] hover:text-white transition-colors duration-300"
      >
        <Download size={13} /> {t("tryon_download")}
      </a>
      {onAdd && (
        <button
          onClick={() => addToCart(p)}
          data-testid={`${prefix}-cart`}
          className="flex items-center gap-1.5 rounded-full bg-[#1A1B18] text-white text-[10px] uppercase tracking-[0.12em] px-4 py-2.5 hover:bg-[#A86946] transition-colors duration-300"
        >
          <ShoppingBag size={13} /> {t("vet_add")}
        </button>
      )}
    </div>
  );

  const hasResults = result || compareResults;

  return (
    <div
      data-testid="tryon-modal"
      className="fixed inset-0 z-[100] bg-[#1A1B18]/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
    >
      <div
        className={`bg-[#FAF9F6] w-full ${isCompare && compareResults ? "max-w-3xl" : "max-w-lg"} rounded-t-3xl md:rounded-2xl max-h-[92vh] overflow-y-auto p-6 md:p-8 transition-[max-width] duration-300`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <ScanFace size={22} className="text-[#A86946]" />
            <h3 className="font-display text-2xl font-medium tracking-tight">{t("tryon_title")}</h3>
          </div>
          <button onClick={onClose} data-testid="tryon-close" aria-label="Chiudi" className="p-2 hover:opacity-60 transition-opacity">
            <X size={20} />
          </button>
        </div>

        {experimental && !hasResults && (
          <div data-testid="tryon-mode-switch" className="flex gap-2 mb-4">
            <button
              onClick={() => setMode("single")}
              data-testid="tryon-mode-single"
              className={`rounded-full text-[10px] uppercase tracking-[0.15em] px-4 py-2 border transition-colors duration-300 ${
                mode === "single" ? "bg-[#1A1B18] text-white border-[#1A1B18]" : "border-[#1A1B18]/25 text-[#1A1B18]/60 hover:border-[#A86946]"
              }`}
            >
              {t("tryon_mode_single")}
            </button>
            <button
              onClick={() => setMode("compare")}
              data-testid="tryon-mode-compare"
              className={`rounded-full text-[10px] uppercase tracking-[0.15em] px-4 py-2 border transition-colors duration-300 ${
                mode === "compare" ? "bg-[#1A1B18] text-white border-[#1A1B18]" : "border-[#1A1B18]/25 text-[#1A1B18]/60 hover:border-[#A86946]"
              }`}
            >
              {t("tryon_mode_compare")}
            </button>
          </div>
        )}

        <p className="text-sm text-[#1A1B18]/60 mb-6">
          {isCompare ? t("tryon_pick_sub") : t("tryon_sub")}{" "}
          {!isCompare && <span className="font-medium text-[#1A1B18]">{product.nome}</span>}
        </p>

        {compareResults ? (
          <div>
            <div data-testid="tryon-compare-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {compareResults.map((r, i) => {
                const p = tryonProducts.find((x) => x.id === r.product_id) || { id: r.product_id, nome: r.nome };
                return (
                  <div key={r.product_id} data-testid={`tryon-cmp-card-${i}`} className="border border-[#DCD7CC] rounded-2xl overflow-hidden bg-white/50">
                    <img src={r.image} alt={r.nome} data-testid={`tryon-cmp-img-${i}`} className="w-full aspect-square object-cover" />
                    <div className="p-4">
                      <p className="font-medium text-sm tracking-tight mb-3">{r.nome}</p>
                      <ActionRow p={p} img={r.image} prefix={`tryon-cmp-${i}`} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <button onClick={reset} data-testid="tryon-retry" className="flex items-center gap-2 rounded-full border border-[#1A1B18]/30 text-xs uppercase tracking-[0.15em] px-5 py-3 hover:bg-[#1A1B18] hover:text-white transition-colors duration-300">
                <RotateCcw size={14} /> {t("tryon_retry")}
              </button>
              <button onClick={onClose} data-testid="tryon-done" className="rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.15em] px-5 py-3 hover:bg-[#1A1B18] transition-colors duration-300">
                {t("tryon_done")}
              </button>
            </div>
          </div>
        ) : result ? (
          <div>
            <img src={result} alt={t("tryon_result_alt")} data-testid="tryon-result" className="w-full rounded-xl border border-[#DCD7CC]" />
            {experimental && (
              <div className="mt-4">
                <ActionRow p={product} img={result} prefix="tryon" />
              </div>
            )}
            <div className="mt-5 flex flex-wrap gap-3">
              <button onClick={reset} data-testid="tryon-retry" className="flex items-center gap-2 rounded-full border border-[#1A1B18]/30 text-xs uppercase tracking-[0.15em] px-5 py-3 hover:bg-[#1A1B18] hover:text-white transition-colors duration-300">
                <RotateCcw size={14} /> {t("tryon_retry")}
              </button>
              <button onClick={onClose} data-testid="tryon-done" className="rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.15em] px-5 py-3 hover:bg-[#1A1B18] transition-colors duration-300">
                {t("tryon_done")}
              </button>
            </div>
          </div>
        ) : loading ? (
          <div data-testid="tryon-loading" className="py-16 text-center">
            <div className="w-10 h-10 border-2 border-[#A86946] border-t-transparent rounded-full animate-spin mx-auto mb-5" />
            <p className="text-sm text-[#1A1B18]/60">{isCompare ? t("tryon_compare_loading") : t("tryon_loading")}</p>
          </div>
        ) : selfie ? (
          <div>
            <img src={selfie} alt="Selfie" data-testid="tryon-preview" className="w-full rounded-xl border border-[#DCD7CC] max-h-[45vh] object-cover" />
            <div className="mt-5 flex flex-wrap gap-3">
              {isCompare ? (
                <button onClick={runCompare} data-testid="tryon-compare-submit" className="flex items-center gap-2 rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.15em] px-6 py-3.5 hover:bg-[#1A1B18] transition-colors duration-300">
                  <ScanFace size={15} /> {t("tryon_compare_start")} ({picked.length})
                </button>
              ) : (
                <button onClick={runTryOn} data-testid="tryon-submit" className="flex items-center gap-2 rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.15em] px-6 py-3.5 hover:bg-[#1A1B18] transition-colors duration-300">
                  <ScanFace size={15} /> {t("tryon_start")}
                </button>
              )}
              <button onClick={reset} data-testid="tryon-change" className="rounded-full border border-[#1A1B18]/30 text-xs uppercase tracking-[0.15em] px-5 py-3 hover:bg-[#1A1B18] hover:text-white transition-colors duration-300">
                {t("tryon_change")}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {isCompare && (
              <div data-testid="tryon-picker" className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[30vh] overflow-y-auto">
                {tryonProducts.map((p, i) => {
                  const on = picked.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => togglePick(p.id)}
                      data-testid={`tryon-pick-${i}`}
                      aria-pressed={on}
                      className={`relative border rounded-xl overflow-hidden transition-colors duration-300 ${
                        on ? "border-[#A86946] ring-1 ring-[#A86946]" : "border-[#DCD7CC] hover:border-[#A86946]/60"
                      }`}
                    >
                      <img src={`${API}/files/${p.image_path}`} alt={p.nome} loading="lazy" className="w-full h-20 object-cover" />
                      <span className="block text-[10px] font-medium tracking-tight px-2 py-1.5 text-left truncate">{p.nome}</span>
                      {on && (
                        <span className="absolute top-1.5 right-1.5 rounded-full bg-[#A86946] text-white p-0.5">
                          <Check size={12} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {cameraOn ? (
              <div>
                <video ref={videoRef} autoPlay playsInline muted data-testid="tryon-video" className="w-full rounded-xl border border-[#DCD7CC] max-h-[45vh] object-cover" />
                <div className="mt-4 flex gap-3">
                  <button onClick={capture} data-testid="tryon-capture" className="flex-1 rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.15em] px-6 py-3.5 hover:bg-[#1A1B18] transition-colors duration-300">
                    {t("tryon_capture")}
                  </button>
                  <button onClick={stopCamera} data-testid="tryon-camera-cancel" className="rounded-full border border-[#1A1B18]/30 text-xs uppercase tracking-[0.15em] px-5 py-3">
                    {t("tryon_cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={startCamera} data-testid="tryon-camera" className="flex flex-col items-center gap-3 border border-[#DCD7CC] rounded-2xl py-8 hover:border-[#A86946] hover:bg-white/60 transition-colors duration-300">
                  <Camera size={26} className="text-[#A86946]" />
                  <span className="text-xs uppercase tracking-[0.15em]">{t("tryon_use_camera")}</span>
                </button>
                <button onClick={() => fileRef.current?.click()} data-testid="tryon-upload" className="flex flex-col items-center gap-3 border border-[#DCD7CC] rounded-2xl py-8 hover:border-[#A86946] hover:bg-white/60 transition-colors duration-300">
                  <Upload size={26} className="text-[#A86946]" />
                  <span className="text-xs uppercase tracking-[0.15em]">{t("tryon_use_upload")}</span>
                </button>
                <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" data-testid="tryon-file" onChange={onUpload} />
              </div>
            )}
            {cameraErr && <p className="text-xs text-[#A86946]">{t("tryon_camera_err")}</p>}
            <p className="text-[11px] text-[#1A1B18]/40 leading-relaxed">{t("tryon_privacy")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
